import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { SEO_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, geoFrance } from '@afficheservices/db/schema'
import { eq, isNull, sql } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SearchAutocomplete } from '~/components/search-autocomplete'
import { BreadcrumbSchema } from '~/components/structured-data'
import { Star, CheckCircle, Clock, MapPin, ArrowRight, Shield, Users } from 'lucide-react'
import { LeadCaptureBlock, CallbackForm, SeoExitIntent } from '~/components/lead-capture-block'
import { getSeoContent } from '~/lib/seo-content.server'
import { formatRating } from '~/lib/format'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'

/**
 * Programmatic SEO route: /:serviceCity
 * Matches patterns like /plombier-paris, /electricien-lyon, /peintre-marseille,
 * /couvreur-antibes. Also matches slug-based /plomberie-paris.
 * Uses BOTH serviceCategories.slug AND SERVICE_LABELS.artisan for matching.
 */
export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.serviceCity ?? ''

  const parentCats = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))

  let matchedCategory = null
  let cityPart = ''

  // Try category slug first (plomberie-paris)
  for (const cat of parentCats) {
    if (slug.startsWith(cat.slug + '-')) {
      matchedCategory = cat
      cityPart = slug.slice(cat.slug.length + 1)
      break
    }
  }

  // Fallback: try artisan name (plombier-paris, couvreur-antibes)
  if (!matchedCategory) {
    for (const [catSlug, svc] of Object.entries(SERVICE_LABELS)) {
      const artisanSlug = svc.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
      if (slug.startsWith(artisanSlug + '-')) {
        matchedCategory = parentCats.find(c => c.slug === catSlug) ?? null
        if (matchedCategory) {
          cityPart = slug.slice(artisanSlug.length + 1)
          break
        }
      }
    }
  }

  if (!matchedCategory || !cityPart) {
    throw new Response('Not Found', { status: 404 })
  }

  // Find city
  const cityName = cityPart.replace(/-/g, ' ')
  const cities = await db
    .select()
    .from(geoFrance)
    .where(sql`LOWER(${geoFrance.communeName}) = ${cityName.toLowerCase()}`)
    .limit(1)

  // 410 only if every namesake is DOM-TOM (keeps mainland Saint-Denis etc.).
  await assertCityNotDomTom(cityName)

  const city = cities[0] ?? { communeName: cityName.charAt(0).toUpperCase() + cityName.slice(1), postalCode: '00000', departmentName: '', regionName: '' }

  // Find pros in this category near this city
  const pros = await db
    .select({
      id: professionalProfiles.id,
      companyName: professionalProfiles.companyName,
      slug: professionalProfiles.slug,
      city: professionalProfiles.city,
      departmentCode: professionalProfiles.departmentCode,
      ratingAvg: professionalProfiles.ratingAvg,
      reviewCount: professionalProfiles.reviewCount,
      responseTimeAvgHours: professionalProfiles.responseTimeAvgHours,
      identityVerified: professionalProfiles.identityVerified,
      shortDescription: professionalProfiles.shortDescription,
      yearsExperience: professionalProfiles.yearsExperience,
    })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(eq(professionalCategories.categoryId, matchedCategory.id))
    .orderBy(sql`${professionalProfiles.ratingAvg} DESC NULLS LAST`)
    .limit(10)

  // Get subcategories
  const subs = await db
    .select({ name: serviceCategories.nameFr })
    .from(serviceCategories)
    .where(eq(serviceCategories.parentId, matchedCategory.id))

  const displayCity = city.communeName ?? cityName.charAt(0).toUpperCase() + cityName.slice(1)

  return {
    category: matchedCategory,
    city: { name: displayCity, postalCode: city.postalCode, department: city.departmentName, region: city.regionName },
    pros,
    subcategories: subs,
    slug,
    aiContent: await getSeoContent(matchedCategory.slug, displayCity),
  }
}

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: 'Page introuvable — AfficheServices' }]
  const { category, city } = data
  const title = `${category.name} à ${city.name} — Devis gratuit | AfficheServices`
  const desc = `Trouvez les meilleurs professionnels en ${category.name.toLowerCase()} à ${city.name}. Comparez ${data.pros.length}+ artisans vérifiés. Devis gratuits sous 48h.`
  return [
    { title },
    { name: 'description', content: desc },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    // Index only when this (service, city) has rich AI content — otherwise the
    // page is a thin template Google rejects ("Crawled - currently not
    // indexed"). noindex,follow keeps link equity flowing without asking
    // Google to index a thin page.
    { name: 'robots', content: data.aiContent ? 'index, follow' : 'noindex, follow' },
  ]
}

export default function ServiceCityPage() {
  const { category, city, pros, subcategories, slug, aiContent } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Services', url: 'https://www.afficheservices.fr/services' },
        { name: category.name, url: `https://www.afficheservices.fr/services/${category.slug}` },
        { name: `${category.name} à ${city.name}`, url: `https://www.afficheservices.fr/${slug}` },
      ]} />

      {/* Hero */}
      <section className="bg-slate-900 py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <nav className="mb-4 flex items-center gap-2 text-xs text-slate-400">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white">Services</Link>
            <span>/</span>
            <Link to={`/services/${category.slug}`} className="hover:text-white">{category.name}</Link>
            <span>/</span>
            <span className="text-white">{city.name}</span>
          </nav>

          <h1 className="text-3xl font-bold text-white sm:text-4xl">
            {category.name} à {city.name}
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-300">
            Comparez les devis de {pros.length > 0 ? `${pros.length}+` : ''} professionnels en {category.name.toLowerCase()} vérifiés à {city.name}
            {city.department ? ` (${city.department})` : ''}.
            Service gratuit, sans engagement.
          </p>

          <div className="mt-6 flex items-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-emerald-400" /> Artisans vérifiés
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-blue-400" /> Réponse sous 48h
            </div>
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 text-amber-400" /> Avis clients vérifiés
            </div>
          </div>

          <div className="mt-8 max-w-xl">
            <SearchAutocomplete />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 lg:px-6">
        {/* Subcategories */}
        {subcategories.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              Specialites {category.name.toLowerCase()} a {city.name}
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {subcategories.map((sub) => (
                <span key={sub.name} className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-xs">
                  {sub.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Pro List */}
        <h2 className="text-xl font-bold text-slate-900">
          {pros.length > 0 ? `${pros.length} professionnel${pros.length > 1 ? 's' : ''} en ${category.name.toLowerCase()}` : 'Aucun professionnel disponible'}
          {' '}a {city.name}
        </h2>

        {pros.length === 0 ? (
          <div className="mt-6 rounded-2xl border-2 border-dashed border-border bg-white p-12 text-center">
            <Users className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-4 text-lg font-semibold text-slate-900">Pas encore de professionnel dans cette zone</p>
            <p className="mt-2 text-sm text-slate-500">Soumettez votre demande et nous chercherons des pros pour vous</p>
            <Link to="/demande-devis/1" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
              Demander un devis <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {pros.map((pro) => (
              <Link key={pro.id} to={`/pro/${pro.slug}`} className="card-hover block rounded-2xl border border-border bg-white p-6 shadow-xs">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
                      {pro.companyName[0]}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{pro.companyName}</h3>
                        {pro.identityVerified && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary">
                            <CheckCircle className="h-3 w-3" /> Verifie
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                        <MapPin className="h-3.5 w-3.5" /> {pro.city} ({pro.departmentCode})
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <div className="flex items-center gap-1">
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                          <span className="font-semibold text-slate-900">{formatRating(pro.ratingAvg)}</span>
                          <span className="text-slate-400">({pro.reviewCount} avis)</span>
                        </div>
                        {pro.responseTimeAvgHours && (
                          <>
                            <span className="text-slate-200">|</span>
                            <div className="flex items-center gap-1 text-slate-500">
                              <Clock className="h-3.5 w-3.5" /> ~{pro.responseTimeAvgHours}h
                            </div>
                          </>
                        )}
                        {pro.yearsExperience && (
                          <>
                            <span className="text-slate-200">|</span>
                            <span className="text-slate-500">{pro.yearsExperience} ans exp.</span>
                          </>
                        )}
                      </div>
                      {pro.shortDescription && (
                        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{pro.shortDescription}</p>
                      )}
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm btn-press">
                    Devis gratuit <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 rounded-2xl bg-slate-900 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-bold text-white">
            Besoin d'un {category.name.toLowerCase()} a {city.name} ?
          </h2>
          <p className="mt-2 text-slate-400">Recevez 3 devis gratuits de professionnels vérifiés</p>
          <Link to="/demande-devis/1" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Internal linking - other cities */}
        <div className="mt-10">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
            {category.name} dans d'autres villes
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Bordeaux', 'Lille', 'Strasbourg', 'Montpellier'].map((c) => {
              const cSlug = c.toLowerCase()
              if (cSlug === city.name.toLowerCase()) return null
              return (
                <Link key={c} to={`/${category.slug}-${cSlug}`}
                  className="rounded-full border border-border bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-primary/30 hover:text-primary shadow-xs">
                  {category.name} a {c}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* AI expert content */}
      {aiContent && (
        <div className="mx-auto max-w-4xl px-4 pb-4">
          <article className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName={city.name} serviceName={category.name} />
            <h2 className="mt-3 text-lg font-bold text-slate-900">{category.name} à {city.name} — guide expert</h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug={category.slug} />
          </article>
          {aiContent.localContextParas && (
            <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="text-base font-bold text-blue-900">Spécificités locales — {city.name}</h3>
              <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
                {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          )}
          {aiContent.priceAnalysis && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
              <h3 className="text-base font-bold text-emerald-900">Analyse des prix — {city.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
            </div>
          )}
          <InternaunteQuestions serviceSlug={category.slug} cityName={city.name} artisan={category.name.toLowerCase()} />
        </div>
      )}

      {/* Lead capture */}
      <div className="mx-auto max-w-4xl px-4 pb-8">
        <LeadCaptureBlock serviceName={category.name} cityName={city.name} categorySlug={category.slug} />
        <div className="mt-6">
          <CallbackForm serviceName={category.name} cityName={city.name} categorySlug={category.slug} />
        </div>
      </div>

      <SeoExitIntent serviceName={category.name} cityName={city.name} categorySlug={category.slug} />
      <Footer />
    </div>
  )
}
